import React, { useMemo, useState } from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    Drawer,
    Group,
    Loader,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconMail, IconSettings, IconUserPlus, IconX } from '@tabler/icons-react';
import useSWR from 'swr';
import {
    AssignableRole,
    Member,
    assignRole,
    createMember,
    grantPermission,
    listAssignableRoles,
    listMembers,
    revokePermission,
} from '../../data/Member';
import {
    Invitation,
    createInvitation,
    listInvitations,
    resendInvitation,
    revokeInvitation,
} from '../../data/Invitation';
import { apiErrorMessage } from '../../data/apiClient';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useCsrf } from '../../hooks/useCsrf';
import { track } from '../../utils/mixpanel';
import { AnalyticsEvents } from '../../analytics/events';
import ManageMemberPanel from './ManageMemberPanel';
import RolePicker from './RolePicker';

const MembersPage: React.FC = () => {
    const { user } = useAuth();
    const { can, isSuperAdmin } = usePermissions();
    const { csrfToken } = useCsrf();

    const {
        data: members,
        isLoading: membersLoading,
        mutate: refreshMembers,
    } = useSWR('api/organization/members', listMembers);
    const { data: roles } = useSWR('api/organization/assignable-roles', listAssignableRoles);

    const { data: invitations, mutate: refreshInvitations } = useSWR(
        'api/organization/invitations',
        listInvitations,
    );

    const [editing, setEditing] = useState<Member | null>(null);
    const [busy, setBusy] = useState(false);
    const [addOpened, setAddOpened] = useState(false);
    const [inviteOpened, setInviteOpened] = useState(false);

    const inviteForm = useForm({
        initialValues: { email: '', role: 'Viewer' },
        validate: {
            email: value => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Enter a valid email'),
            role: value => (value ? null : 'Pick a role'),
        },
    });

    // Declared before the permission early-return below to keep hook order stable.
    const addForm = useForm({
        initialValues: { name: '', email: '', role: 'Owner' },
        validate: {
            name: value => (value.trim().length === 0 ? 'Name is required' : null),
            email: value => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Enter a valid email'),
            role: value => (value ? null : 'Pick a role'),
        },
    });

    // The full permission catalog is the union of every assignable role's perms.
    const catalog = useMemo(() => {
        const set = new Set<string>();
        (roles ?? []).forEach((role: AssignableRole) =>
            role.permissions.forEach(permission => set.add(permission)),
        );
        return Array.from(set).sort();
    }, [roles]);

    if (!can('users.manage')) {
        return (
            <Stack px="md">
                <Title order={3}>Members</Title>
                <Text c="dimmed">You don’t have permission to manage members.</Text>
            </Stack>
        );
    }

    // Re-read the member being edited from fresh SWR data so the drawer updates
    // immediately after each mutation.
    const current = editing ? members?.find(m => m.id === editing.id) ?? editing : null;

    const run = async (action: () => Promise<void>, successMessage: string) => {
        setBusy(true);
        try {
            await action();
            await refreshMembers();
            notifications.show({ message: successMessage });
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Action failed',
                message: apiErrorMessage(err),
            });
        } finally {
            setBusy(false);
        }
    };

    const setMemberRole = (member: Member, role: string) =>
        run(() => assignRole(csrfToken, member.id, role), `Role updated to ${role}`);

    const grant = (member: Member, permission: string) =>
        run(() => grantPermission(csrfToken, member.id, permission), `Granted ${permission}`);

    const revoke = (member: Member, permission: string) =>
        run(() => revokePermission(csrfToken, member.id, permission), `Revoked ${permission}`);

    const closeAdd = () => {
        addForm.reset();
        setAddOpened(false);
    };

    const closeInvite = () => {
        inviteForm.reset();
        setInviteOpened(false);
    };

    /**
     * Unlike handleAdd below, this creates no user row — the account is created
     * when the invitee clicks the emailed link and sets a password.
     */
    const handleInvite = async (values: typeof inviteForm.values) => {
        setBusy(true);
        try {
            const invite = await createInvitation(csrfToken, {
                email: values.email.trim(),
                role: values.role,
            });
            await refreshInvitations();
            track(AnalyticsEvents.MemberInvited, {
                role: values.role,
                emailSent: invite?.emailSent !== false,
            });

            // The invite exists either way; only delivery may have failed. Say
            // so rather than reporting a send that did not happen.
            if (invite?.emailSent === false) {
                notifications.show({
                    color: 'yellow',
                    title: 'Invitation created, but not emailed',
                    message: `Could not deliver to ${values.email.trim()}. Check the mail configuration, then use Resend.`,
                    autoClose: false,
                });
            } else {
                notifications.show({ message: `Invitation sent to ${values.email.trim()}.` });
            }
            closeInvite();
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Could not send invitation',
                message: apiErrorMessage(err),
            });
        } finally {
            setBusy(false);
        }
    };

    const handleRevokeInvite = (invite: Invitation) =>
        run(async () => {
            await revokeInvitation(csrfToken, invite.id);
            await refreshInvitations();
        }, `Revoked the invitation for ${invite.email}.`);

    const handleResendInvite = (invite: Invitation) =>
        run(async () => {
            await resendInvitation(csrfToken, invite.id);
            await refreshInvitations();
        }, `Sent a new invitation to ${invite.email}.`);

    // A member is created directly rather than invited by email; they sign in
    // via SSO, which matches on their email address.
    const handleAdd = async (values: typeof addForm.values) => {
        setBusy(true);
        try {
            await createMember(csrfToken, {
                name: values.name.trim(),
                email: values.email.trim(),
                role: values.role,
            });
            await refreshMembers();
            notifications.show({ message: `Added ${values.name.trim()}.` });
            closeAdd();
        } catch (err: any) {
            // Surface the API message verbatim — it names the org when the email
            // already belongs to someone else.
            notifications.show({
                color: 'red',
                title: 'Could not add member',
                message: apiErrorMessage(err),
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack px="md" gap="lg">
            <Group justify="space-between" align="center">
                <Title order={3}>Members</Title>
                <Group gap="xs">
                    {/*
                     * Direct provisioning stays super-admin only: it creates an
                     * SSO-only account with no password and no notification.
                     * Inviting is the normal path and is open to anyone holding
                     * users.manage, which reaching this page already required.
                     */}
                    {isSuperAdmin && (
                        <Button
                            variant="default"
                            leftSection={<IconUserPlus size={16} />}
                            onClick={() => setAddOpened(true)}
                        >
                            Add SSO member
                        </Button>
                    )}
                    <Button leftSection={<IconMail size={16} />} onClick={() => setInviteOpened(true)}>
                        Invite member
                    </Button>
                </Group>
            </Group>

            {membersLoading ? (
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            ) : (
                <Table highlightOnHover verticalSpacing="sm">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Email</Table.Th>
                            <Table.Th>Roles</Table.Th>
                            <Table.Th w={80} />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {(members ?? []).map(member => {
                            const isSelf = member.id === user?.id;
                            return (
                                <Table.Tr key={member.id}>
                                    <Table.Td>{member.name}</Table.Td>
                                    <Table.Td>{member.email}</Table.Td>
                                    <Table.Td>
                                        <Group gap="xs">
                                            {member.roles.length === 0 && (
                                                <Text size="sm" c="dimmed">
                                                    —
                                                </Text>
                                            )}
                                            {member.roles.map(role => (
                                                <Badge key={role} variant="light">
                                                    {role}
                                                </Badge>
                                            ))}
                                        </Group>
                                    </Table.Td>
                                    <Table.Td>
                                        <Tooltip
                                            label="You can’t edit your own roles"
                                            disabled={!isSelf}
                                        >
                                            <ActionIcon
                                                variant="subtle"
                                                disabled={isSelf}
                                                onClick={() => setEditing(member)}
                                                aria-label={`Manage ${member.name}`}
                                            >
                                                <IconSettings size={18} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Table.Td>
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            )}

            {/* Only pending invites come back from the API; accepted, revoked
                and expired ones are filtered server-side. */}
            {(invitations ?? []).length > 0 && (
                <Stack gap="xs">
                    <Title order={5}>Pending invitations</Title>
                    <Table highlightOnHover verticalSpacing="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Email</Table.Th>
                                <Table.Th>Role</Table.Th>
                                <Table.Th>Expires</Table.Th>
                                <Table.Th w={160} />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {(invitations ?? []).map(invite => (
                                <Table.Tr key={invite.id}>
                                    <Table.Td>{invite.email}</Table.Td>
                                    <Table.Td>
                                        <Badge variant="outline" color="gray">
                                            {invite.role}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size="sm" c="dimmed">
                                            {new Date(invite.expiresAt).toLocaleDateString()}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap={4} justify="flex-end">
                                            <Tooltip
                                                label="Send a new link. The previous one stops working."
                                                withArrow
                                            >
                                                <Button
                                                    size="compact-sm"
                                                    variant="subtle"
                                                    disabled={busy}
                                                    onClick={() => handleResendInvite(invite)}
                                                >
                                                    Resend
                                                </Button>
                                            </Tooltip>
                                            <Tooltip label="Revoke this invitation" withArrow>
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    disabled={busy}
                                                    onClick={() => handleRevokeInvite(invite)}
                                                    aria-label={`Revoke the invitation for ${invite.email}`}
                                                >
                                                    <IconX size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Stack>
            )}

            <Drawer
                opened={inviteOpened}
                onClose={closeInvite}
                position="right"
                title="Invite member"
                padding="lg"
                size="lg"
            >
                <form onSubmit={inviteForm.onSubmit(handleInvite)}>
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            They’ll get an email with a link to set a password and join this
                            organization. The link expires in 7 days.
                        </Text>
                        <TextInput
                            label="Email"
                            placeholder="person@company.com"
                            disabled={busy}
                            {...inviteForm.getInputProps('email')}
                        />
                        <RolePicker
                            roles={(roles ?? []).map((role: AssignableRole) => role.name)}
                            value={inviteForm.values.role}
                            onChange={role => inviteForm.setFieldValue('role', role)}
                            disabled={busy}
                            error={inviteForm.errors.role}
                            name="invite-role"
                        />
                        <Group justify="flex-end">
                            <Button variant="default" onClick={closeInvite} disabled={busy}>
                                Cancel
                            </Button>
                            <Button type="submit" loading={busy}>
                                Send invitation
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Drawer>

            <Drawer
                opened={!!current}
                onClose={() => setEditing(null)}
                position="right"
                title={current ? `Manage ${current.name}` : ''}
                padding="lg"
                size="lg"
            >
                {current && (
                    <ManageMemberPanel
                        member={current}
                        assignableRoles={(roles ?? []).map((role: AssignableRole) => role.name)}
                        permissionCatalog={catalog}
                        busy={busy}
                        onRoleChange={role => setMemberRole(current, role)}
                        onGrantPermission={permission => grant(current, permission)}
                        onRevokePermission={permission => revoke(current, permission)}
                    />
                )}
            </Drawer>

            <Drawer
                opened={addOpened}
                onClose={closeAdd}
                position="right"
                title="Add member"
                padding="lg"
                size="lg"
            >
                <form onSubmit={addForm.onSubmit(handleAdd)}>
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            The member signs in with Google or Microsoft using this email
                            address. No invitation email is sent.
                        </Text>
                        <TextInput
                            label="Name"
                            placeholder="Dana Scully"
                            disabled={busy}
                            {...addForm.getInputProps('name')}
                        />
                        <TextInput
                            label="Email"
                            placeholder="dana@example.com"
                            disabled={busy}
                            {...addForm.getInputProps('email')}
                        />
                        <RolePicker
                            roles={(roles ?? []).map((role: AssignableRole) => role.name)}
                            value={addForm.values.role}
                            onChange={role => addForm.setFieldValue('role', role)}
                            disabled={busy}
                            error={addForm.errors.role}
                            name="add-role"
                        />
                        <Group justify="flex-end">
                            <Button variant="default" onClick={closeAdd} disabled={busy}>
                                Cancel
                            </Button>
                            <Button type="submit" loading={busy}>
                                Add member
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Drawer>
        </Stack>
    );
};

export default MembersPage;
